import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronRight, Search } from "lucide-react";
import { Collapsible } from "radix-ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useActiveBuInstances } from "@/hooks/use-bu-instances";
import { SEARCH_OPERATOR_OPTIONS } from "@/lib/enums";
import { cn } from "@/lib/utils";
import { type ClientSearchRequest, clientSearchRequestSchema } from "@/schemas/clients";

interface ClientSearchFormProps {
  onSearch: (values: ClientSearchRequest) => void;
  isSearching: boolean;
}

/** `SEARCH_OPERATOR_OPTIONS` is a required-selection enum, but these fields
 * are optional — `EnumSelect`'s `allOption` maps "" <-> undefined for us. */
function toOptionalOperator(value: string): ClientSearchRequest["dateCreatedOperator"] {
  return value === "" ? undefined : (value as ClientSearchRequest["dateCreatedOperator"]);
}

/** Mirrors `ContractSearchForm` exactly, field set drawn from `ClientSearchCriteria`. */
export function ClientSearchForm({ onSearch, isSearching }: ClientSearchFormProps) {
  const { label } = useBuInstanceLabel();
  const { data: buInstances, isLoading: isLoadingBuInstances } = useActiveBuInstances();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const form = useForm<ClientSearchRequest>({
    resolver: zodResolver(clientSearchRequestSchema),
    defaultValues: { buInstanceId: "" },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSearch)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <FormField
            control={form.control}
            name="buInstanceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={isLoadingBuInstances ? "Loading…" : `Select a ${label}`}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(buInstances ?? []).map((bu) => (
                      <SelectItem key={bu.id} value={bu.id}>
                        {bu.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Client ID</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. CL-1042" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="surname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Surname</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Smith" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Acme Ltd" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Post Code</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Collapsible.Root open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <Collapsible.Trigger asChild>
            <Button type="button" variant="ghost" size="sm" className="gap-1 px-0">
              <ChevronRight
                className={cn("h-4 w-4 transition-transform", advancedOpen && "rotate-90")}
              />
              Advanced search
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content className="pt-3">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <FormField
                control={form.control}
                name="initials"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initials</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="telNo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tel No</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="col-span-2 space-y-2 sm:col-span-3 lg:col-span-1">
                <Label>Date Created</Label>
                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="dateCreated"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input type="date" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dateCreatedOperator"
                    render={({ field }) => (
                      <FormItem className="w-32 shrink-0">
                        <FormControl>
                          <EnumSelect
                            value={field.value ?? ""}
                            onValueChange={(value) => field.onChange(toOptionalOperator(value))}
                            options={SEARCH_OPERATOR_OPTIONS}
                            allOption={{ label: "Not set" }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="col-span-2 space-y-2 sm:col-span-3 lg:col-span-1">
                <Label>Last Updated</Label>
                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="lastUpdated"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input type="date" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastUpdatedOperator"
                    render={({ field }) => (
                      <FormItem className="w-32 shrink-0">
                        <FormControl>
                          <EnumSelect
                            value={field.value ?? ""}
                            onValueChange={(value) => field.onChange(toOptionalOperator(value))}
                            options={SEARCH_OPERATOR_OPTIONS}
                            allOption={{ label: "Not set" }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </Collapsible.Content>
        </Collapsible.Root>

        <div className="flex justify-end">
          <Button type="submit" disabled={isSearching}>
            <Search className="h-4 w-4 mr-1" />
            {isSearching ? "Searching…" : "Search"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
